/* eslint-disable no-use-before-define */
import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';

/**
 * @typedef {object} AutocompleteOption
 * @property {string} display
 * @property {string | number | null} value
 */

/**
 * @typedef {object} AutocompleteDropdownProps
 * @property {string=} id
 * @property {AutocompleteOption[]=} selectData
 * @property {AutocompleteOption | string | number | null=} selectedData
 * @property {(option: AutocompleteOption) => void=} onChange
 * @property {string=} label
 * @property {'small' | 'medium'=} size
 * @property {boolean=} disabled
 * @property {object=} inputProps
 * @property {(event: React.SyntheticEvent, value: string) => void=} onInputChange
 * @property {(options: AutocompleteOption[]) => AutocompleteOption[]=} filterOptions
 * @property {boolean=} loading
 * @property {object=} listboxProps
 */

/** @param {AutocompleteDropdownProps} props */
const AutocompleteDropdown = ({
  id,
  selectData = [],
  selectedData,
  onChange,
  label,
  size = 'medium',
  disabled = false,
  inputProps = {},
  onInputChange,
  filterOptions,
  loading = false,
  listboxProps = {},
}) => (
  <Autocomplete
    ListboxProps={listboxProps}
    id={`autocomplete-selector-${id}`}
    disabled={disabled}
    options={selectData}
    value={
      selectedData
        ? typeof selectedData === 'object'
          ? selectedData
          : {
              display: String(selectedData),
              value: selectedData,
            }
        : null
    }
    getOptionLabel={(option) => option.display}
    style={{ width: '100%' }}
    size={size}
    onChange={(event, itemSelected) => {
      if (onChange) {
        onChange(itemSelected ?? { value: null, display: '' });
      }
    }}
    onInputChange={onInputChange}
    filterOptions={filterOptions}
    loading={loading}
    renderInput={(params) => (
      <TextField {...params} label={label} variant="outlined" disabled={disabled} {...inputProps} />
    )}
    isOptionEqualToValue={(option, value) => option.value === value.value}
  />
);

AutocompleteDropdown.propTypes = {
  id: PropTypes.string,
  selectData: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
    }),
  ),
  selectedData: PropTypes.oneOfType([
    PropTypes.shape({
      display: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
    }),
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
  ]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  inputProps: PropTypes.object,
  onInputChange: PropTypes.func,
  filterOptions: PropTypes.func,
  loading: PropTypes.bool,
  listboxProps: PropTypes.object,
};

export default AutocompleteDropdown;
